/**
 * Playground interceptor handlers for Player Segmentation CRUD operations.
 * Matches API pattern: /v1/web/player-segment
 */
import { PlaygroundSessionStore, PlaygroundLimitError } from '../PlaygroundSessionStore.js';

const store = new PlaygroundSessionStore('pg_segments', 5);
const now = () => new Date().toISOString();

/**
 * GET /v1/web/player-segment — list all segments (filtered by X-Web-Game-Id header if present).
 */
export function handleGetSegments(config, match) {
    const gameId = config.headers?.['X-Web-Game-Id'] || config.headers?.['x-web-game-id'];
    let items = store.getAll();
    if (gameId) {
        items = items.filter(item => item.gameId === gameId);
    }
    return { segments: items };
}

/**
 * GET /v1/web/player-segment/active — the segments something can target (live or not yet used), as the
 * real endpoint returns them: drafts and archived segments left out.
 */
export function handleGetActiveSegments(config, match) {
    const usable = new Set(['ACTIVE', 'AVAILABLE']);
    const { segments } = handleGetSegments(config, match);
    return { segments: segments.filter(item => usable.has(String(item.state || 'ACTIVE').toUpperCase())) };
}

/**
 * GET /v1/web/player-segment/:id — single segment by id.
 */
export function handleGetSegmentById(config, match) {
    const id = match[1];
    const item = store.getById(id);
    if (!item) {
        throw new Error(`Segment with id ${id} not found`);
    }
    return item;
}

/**
 * POST /v1/web/player-segment — create new segment.
 */
export function handleCreateSegment(config, match) {
    const data = typeof config.data === 'string' ? JSON.parse(config.data) : (config.data || {});
    const id = PlaygroundSessionStore.getNextId();
    const newItem = {
        ...data,
        id,
        versionInfo: {
            version: 1,
            parentEntityId: `${id}-parent`,
            auditInfo: {
                lastModifiedBy: 'explorer@playground.geoplay.io',
                lastModifiedDate: now(),
            },
            versionDescription: data.changeDescription || 'Initial version',
            latest: true,
        },
    };
    return { segment: store.create(newItem) };
}

/**
 * PUT /v1/web/player-segment/:id — update existing segment.
 */
export function handleUpdateSegment(config, match) {
    const id = match[1];
    const data = typeof config.data === 'string' ? JSON.parse(config.data) : (config.data || {});
    const existing = store.getById(id);
    if (!existing) {
        throw new Error(`Segment with id ${id} not found`);
    }
    const currentVersion = existing.versionInfo?.version || 1;
    const updated = {
        ...data,
        id,
        versionInfo: {
            ...existing.versionInfo,
            version: currentVersion + 1,
            auditInfo: {
                lastModifiedBy: 'explorer@playground.geoplay.io',
                lastModifiedDate: now(),
            },
            versionDescription: data.changeDescription || existing.versionInfo?.versionDescription || '',
            latest: true,
        },
    };
    return { segment: store.update(id, updated) };
}

/**
 * DELETE /v1/web/player-segment/:id — remove segment.
 */
export function handleDeleteSegment(config, match) {
    const id = match[1];
    store.delete(id);
    return { success: true };
}

/**
 * DELETE /v1/web/player-segment/:id/tree — remove a segment and everything scoped under it, as the
 * real endpoint does. The playground has no dependency graph, so nothing here can refuse.
 */
export function handleDeleteSegmentTree(config, match) {
    const id = match[1];
    const all = store.getAll();
    const chainOf = (s) => s?.versionInfo?.parentEntityId || s?.id;
    const root = all.find(s => s.id === id || chainOf(s) === id);
    if (!root) {
        throw new Error(`Segment with id ${id} not found`);
    }
    const doomed = new Set([chainOf(root)]);
    let grew = true;
    while (grew) {
        grew = false;
        all.forEach(s => {
            if (s.parentSegmentId && doomed.has(s.parentSegmentId) && !doomed.has(chainOf(s))) {
                doomed.add(chainOf(s));
                grew = true;
            }
        });
    }
    all.filter(s => doomed.has(chainOf(s))).forEach(s => store.delete(s.id));
    return { success: true };
}

/**
 * POST /v1/web/player-segment/:id/clone — duplicate segment with new id.
 */
export function handleCloneSegment(config, match) {
    const id = match[1];
    const existing = store.getById(id);
    if (!existing) {
        throw new Error(`Segment with id ${id} not found`);
    }
    const newId = PlaygroundSessionStore.getNextId();
    const cloned = {
        ...existing,
        id: newId,
        segmentName: `${existing.segmentName} (Copy)`,
        state: 'draft',
        versionInfo: {
            version: 1,
            parentEntityId: `${newId}-parent`,
            auditInfo: {
                lastModifiedBy: 'explorer@playground.geoplay.io',
                lastModifiedDate: now(),
            },
            versionDescription: `Cloned from ${existing.segmentName}`,
            latest: true,
        },
    };
    return { segment: store.create(cloned) };
}

/**
 * POST /v1/web/player-segment/:id/rollback — rollback (mock: return existing item).
 */
export function handleRollbackSegment(config, match) {
    const id = match[1];
    const existing = store.getById(id);
    if (!existing) {
        throw new Error(`Segment with id ${id} not found`);
    }
    return { segment: existing };
}

/**
 * GET /v1/web/player-segment/:id/versions — return mock version history.
 */
export function handleGetSegmentVersions(config, match) {
    const id = match[1];
    const existing = store.getById(id);
    if (!existing) {
        throw new Error(`Segment with id ${id} not found`);
    }
    return [existing];
}

/**
 * POST /v1/web/player-segment/matching-count — return mock matching player count.
 */
export function handleGetMatchingCount(config, match) {
    const count = Math.floor(Math.random() * (50000 - 1000 + 1)) + 1000;
    return { count };
}
